/**
 * Plot Container
 * Wrapper that renders multiple plots in different viewing modes
 */

import React, { useState } from 'react';
import { Box, Grid, Tabs, Tab } from '@mui/material';
import { colors } from '../theme/colors';

export type ViewMode = 'grid' | 'stacked' | 'tabs';

interface PlotItem {
  key: string | number;
  title: string;
  component: React.ReactNode;
}

interface PlotContainerProps {
  plots: PlotItem[];
  viewMode: ViewMode;
}

export const PlotContainer: React.FC<PlotContainerProps> = ({ plots, viewMode }) => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Grid View - 2 columns responsive
  if (viewMode === 'grid') {
    return (
      <Grid container spacing={3}>
        {plots.map((plot) => (
          <Grid item xs={12} lg={6} key={plot.key}>
            <Box
              sx={{
                '& .MuiBox-root:first-of-type': {
                  mb: 0, // Remove bottom margin from plot component in grid mode
                },
              }}
            >
              {plot.component}
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  }

  // Tabs View - one plot at a time
  if (viewMode === 'tabs') {
    return (
      <Box>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: colors.neutral[200],
            mb: 3,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.9375rem',
                color: colors.text.secondary,
                '&.Mui-selected': {
                  color: colors.primary.main,
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: colors.primary.main,
                height: 3,
              },
            }}
          >
            {plots.map((plot, index) => (
              <Tab key={plot.key} label={plot.title} />
            ))}
          </Tabs>
        </Box>
        <Box>{plots[activeTab]?.component}</Box>
      </Box>
    );
  }

  // Stacked View - default vertical layout
  return (
    <Box>
      {plots.map((plot) => (
        <Box key={plot.key}>{plot.component}</Box>
      ))}
    </Box>
  );
};
